import { call, put, select, takeEvery } from "redux-saga/effects";
import { SubmissionError } from "redux-form";
import { each } from "lodash";
import {
  getContentPacks,
  createContentPack,
  updateContentPack,
  deleteContentPack,
  publishContentPack,
  unpublishContentPack,
  getContentTags,
} from "src/web-services";
import {
  CollegeContentCreators,
  CollegeContentTypes,
} from "../actions";
import { Notification } from "src/helpers";
import i18n from "src/i18n";

const authTokenSelector = state => state.user.authToken;

const submissionError = error => {
  const errorMessages = {};

  each(error?.response?.data?.error, (message, key) => {
    errorMessages[key] =
      message instanceof Array ? message : [message];
  });

  return new SubmissionError(errorMessages);
};

export function* requestGetContentPacks(action) {
  try {
    const authToken = yield select(authTokenSelector);
    const response = yield call(
      getContentPacks,
      authToken,
      action.params,
    );
    yield put(
      CollegeContentCreators.getContentPacksSuccess(response.data),
    );
  } catch (error) {
    yield put(CollegeContentCreators.getContentPacksFailure());
  }
}

export function* requestCreateContentPack(action) {
  const { data, resolve, reject } = action;

  try {
    const authToken = yield select(authTokenSelector);
    const response = yield call(createContentPack, authToken, data);

    resolve && resolve();
    yield put(
      CollegeContentCreators.createContentPackSuccess(response.data),
    );
    Notification.success(i18n.t("college_content.alerts.created"));
  } catch (error) {
    reject && reject(submissionError(error));
    yield put(CollegeContentCreators.createContentPackFailure());
  }
}

export function* requestUpdateContentPack(action) {
  const { packId, data, resolve, reject } = action;

  try {
    const authToken = yield select(authTokenSelector);
    const response = yield call(
      updateContentPack,
      authToken,
      packId,
      data,
    );

    resolve && resolve();
    yield put(
      CollegeContentCreators.updateContentPackSuccess(response.data),
    );
    Notification.success(i18n.t("college_content.alerts.updated"));
  } catch (error) {
    reject && reject(submissionError(error));
    yield put(CollegeContentCreators.updateContentPackFailure());
  }
}

export function* requestDeleteContentPack(action) {
  try {
    const authToken = yield select(authTokenSelector);
    yield call(deleteContentPack, authToken, action.packId);
    yield put(
      CollegeContentCreators.deleteContentPackSuccess(action.packId),
    );
    Notification.success(i18n.t("college_content.alerts.deleted"));
  } catch (error) {
    yield put(CollegeContentCreators.deleteContentPackFailure());
  }
}

export function* requestPublishContentPack(action) {
  try {
    const authToken = yield select(authTokenSelector);
    const response = yield call(
      publishContentPack,
      authToken,
      action.packId,
    );
    yield put(
      CollegeContentCreators.publishContentPackSuccess(response.data),
    );
    Notification.success(i18n.t("college_content.alerts.published"));
  } catch (error) {
    yield put(CollegeContentCreators.publishContentPackFailure());
    Notification.error(
      error?.response?.data?.message ||
        i18n.t("college_content.alerts.publish_failed"),
    );
  }
}

export function* requestUnpublishContentPack(action) {
  try {
    const authToken = yield select(authTokenSelector);
    const response = yield call(
      unpublishContentPack,
      authToken,
      action.packId,
    );
    yield put(
      CollegeContentCreators.unpublishContentPackSuccess(
        response.data,
      ),
    );
    Notification.success(
      i18n.t("college_content.alerts.unpublished"),
    );
  } catch (error) {
    yield put(CollegeContentCreators.unpublishContentPackFailure());
  }
}

export function* requestGetContentTags() {
  try {
    const authToken = yield select(authTokenSelector);
    const response = yield call(getContentTags, authToken);
    yield put(
      CollegeContentCreators.getContentTagsSuccess(response.data),
    );
  } catch (error) {
    yield put(CollegeContentCreators.getContentTagsFailure());
  }
}

export function* watchCollegeContentRequests() {
  yield takeEvery(
    CollegeContentTypes.GET_CONTENT_PACKS_REQUEST,
    requestGetContentPacks,
  );
  yield takeEvery(
    CollegeContentTypes.CREATE_CONTENT_PACK_REQUEST,
    requestCreateContentPack,
  );
  yield takeEvery(
    CollegeContentTypes.UPDATE_CONTENT_PACK_REQUEST,
    requestUpdateContentPack,
  );
  yield takeEvery(
    CollegeContentTypes.DELETE_CONTENT_PACK_REQUEST,
    requestDeleteContentPack,
  );
  yield takeEvery(
    CollegeContentTypes.PUBLISH_CONTENT_PACK_REQUEST,
    requestPublishContentPack,
  );
  yield takeEvery(
    CollegeContentTypes.UNPUBLISH_CONTENT_PACK_REQUEST,
    requestUnpublishContentPack,
  );
  yield takeEvery(
    CollegeContentTypes.GET_CONTENT_TAGS_REQUEST,
    requestGetContentTags,
  );
}
